import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsCalendar3, BsFillPeopleFill, BsArrowRight } from 'react-icons/bs';
import { BiCalendarPlus, BiSearch, BiHome, BiListUl, BiLogOut, BiCopy, BiLinkExternal, BiFileBlank } from 'react-icons/bi';
import { MdOutlineEventNote } from 'react-icons/md';
import axios from 'axios';
import moment from 'moment-timezone';
import { Buffer } from 'buffer';
import LoadingScreen from '../component/LoadingScreen';
import Avatar from '../assets/images/users/avt1.png';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { getImageSrc } from '../utils/imageSrc';
import { useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { callMsGraph, checkRole } from '../callMsGroup';
import { useLabels, useLang, Lang } from '../hooks/useLabels';

interface EventItem {
    id: number;
    eventNumber: string;
    eventName: string;
    description: string;
    startDate: string;
    endDate: string;
    image: string;
    owner: string;
    totalRecieved: number;
    totalMember: number;
    memberGroup: number;
    createdAt?: string;
}

// ── Event Detail Modal ───────────────────────────────────────────────────────
interface EventDetailModalProps {
    event: EventItem;
    onClose: () => void;
    onNavigate: () => void;
    userEmail: string;
    isAdmin: boolean;
}

function EventDetailModal({ event, onClose, onNavigate, userEmail, isAdmin }: EventDetailModalProps) {
    const today = moment().startOf('day');
    const start = moment(event.startDate).startOf('day');
    const end = moment(event.endDate).startOf('day');
    const isLive = start.isSameOrBefore(today) && end.isSameOrAfter(today);
    const isExpired = end.isBefore(today);
    const isOwner = event.owner
        ?.split(';')
        .some((o) => o.trim().toLowerCase() === userEmail.toLowerCase());
    const canManage = isAdmin;
    const progress =
        event.memberGroup !== 3 && event.totalMember > 0
            ? Math.min((event.totalRecieved / event.totalMember) * 100, 100)
            : null;
    const owners = event.owner
        ? event.owner.split(';').map(o => o.trim()).filter(Boolean)
        : [];
    const [copied, setCopied] = React.useState(false);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(`${window.location.origin}/event/${event.eventNumber}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="he-modal-overlay" onClick={onClose}>
            <div className="he-modal" onClick={(e) => e.stopPropagation()}>
                <button className="he-modal-close" onClick={onClose}>×</button>

                {/* Image */}
                <div className="he-modal-img-wrap">
                    <img src={getImageSrc(event.image)} alt={event.eventName} />
                    {isLive && (
                        <span className="he-modal-status he-modal-status--live">
                            <span className="he-card-badge-dot" /> LIVE
                        </span>
                    )}
                    {isExpired && (
                        <span className="he-modal-status he-modal-status--ended">Ended</span>
                    )}
                    {!isLive && !isExpired && (
                        <span className="he-modal-status he-modal-status--upcoming">Upcoming</span>
                    )}
                </div>

                {/* Body */}
                <div className="he-modal-body">
                    <h2 className="he-modal-title">{event.eventName}</h2>

                    <div className="he-modal-meta">
                        <div className="he-modal-meta-item">
                            <BsCalendar3 />
                            <span>
                                {moment.utc(event.startDate).tz('Asia/Bangkok').format('DD MMM YYYY')} –{' '}
                                {moment.utc(event.endDate).tz('Asia/Bangkok').format('DD MMM YYYY')}
                            </span>
                        </div>
                        {owners.length > 0 && (
                            <div className="he-modal-meta-item">
                                <BsFillPeopleFill />
                                <span>
                                    {owners.slice(0, 2).join(', ')}
                                    {owners.length > 2 && ` +${owners.length - 2} more`}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Progress */}
                    <div className="he-modal-progress-wrap">
                        <div className="he-modal-progress-bar">
                            <div
                                className="he-modal-progress-fill"
                                style={{ width: `${progress ?? 100}%` }}
                            />
                        </div>
                        <div className="he-modal-progress-label">
                            <span><strong>{event.totalRecieved}</strong> joined</span>
                            <span>
                                {event.memberGroup !== 3 ? `${event.totalMember} seats` : 'Unlimited'}
                            </span>
                        </div>
                    </div>

                    {event.description && (
                        <p className="he-modal-desc">{event.description}</p>
                    )}

                    {/* Actions — เฉพาะ owner หรือ admin */}
                    {canManage && (
                        <div className="he-modal-actions">
                            {!isExpired && (
                                <button className="he-modal-btn-primary" onClick={onNavigate}>
                                    <BiLinkExternal /> Go to Event
                                </button>
                            )}
                            <button className="he-modal-btn-ghost" onClick={handleCopy}>
                                <BiCopy /> {copied ? 'Copied!' : 'Copy Link'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Topbar ──────────────────────────────────────────────────────────────────
interface TopbarProps {
    photoData: string;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    lang: Lang;
    onLangChange: (l: Lang) => void;
}
function HomeTopbar({ photoData, isAdmin, isSuperAdmin, lang, onLangChange }: TopbarProps) {
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();

    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
            <header className="he-topbar">
                <div className="he-topbar-brand" onClick={() => navigate(isSuperAdmin ? '/forms' : '/')}>
                    <img src="/logo-pandora.png" alt="Pandora" className="he-topbar-brand-logo" />
                </div>
                <nav className="he-topbar-nav">
                    {!isSuperAdmin && (
                        <button className="he-topbar-nav-item active" onClick={() => navigate('/')}>
                            <BiHome size={16} /> <span>Home</span>
                        </button>
                    )}
                    {isSuperAdmin && (
                        <button className="he-topbar-nav-item" onClick={() => navigate('/forms')}>
                            <BiFileBlank size={16} /> <span>Forms</span>
                        </button>
                    )}
                    {isAdmin && (
                        <>
                            <button className="he-topbar-nav-item" onClick={() => navigate('/myevents')}>
                                <BiListUl size={16} /> <span>My Events</span>
                            </button>
                            <button className="he-topbar-nav-item" onClick={() => navigate('/admin')}>
                                <BiCalendarPlus size={16} /> <span>Admin</span>
                            </button>
                        </>
                    )}
                </nav>
                <div className="he-topbar-right">
                    {/* Language toggle */}
                    <div className="he-lang-toggle">
                        <button
                            className={`he-lang-btn${lang === 'en' ? ' active' : ''}`}
                            onClick={() => onLangChange('en')}
                        >
                            EN
                        </button>
                        <span className="he-lang-sep">|</span>
                        <button
                            className={`he-lang-btn${lang === 'vn' ? ' active' : ''}`}
                            onClick={() => onLangChange('vn')}
                        >
                            VN
                        </button>
                    </div>
                    <img src={photoData || Avatar} className="he-topbar-avatar" alt="avatar" />
                    <span className="he-topbar-username">{accounts[0]?.name}</span>
                    <button
                        className="he-topbar-logout"
                        title="Logout"
                        onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: '/' })}
                    >
                        <BiLogOut size={18} />
                    </button>
                </div>
            </header>
        </MsalAuthenticationTemplate>
    );
}

// ── Event Poster Card ────────────────────────────────────────────────────────
interface EventPosterCardProps {
    event: EventItem;
    onClick: () => void;
    badge?: string;
    badgeType?: 'live' | 'upcoming' | 'soon';
}
function EventPosterCard({ event, onClick, badge, badgeType }: EventPosterCardProps) {
    return (
        <div className="he-card" onClick={onClick}>
            <div className="he-card-img-wrap">
                <img
                    src={getImageSrc(event.image)}
                    alt={event.eventName}
                    className="he-card-img"
                />
                {badge && (
                    <span className={`he-card-badge he-card-badge--${badgeType || 'upcoming'}`}>
                        {badgeType === 'live' && <span className="he-card-badge-dot" />}
                        {badge}
                    </span>
                )}
            </div>
            <div className="he-card-body">
                <h3 className="he-card-title">{event.eventName}</h3>
                <p className="he-card-desc">{event.description}</p>
                <div className="he-card-meta">
                    <span className="he-card-meta-item">
                        <BsCalendar3 />
                        {moment.utc(event.startDate).tz('Asia/Bangkok').format('DD MMM YYYY')}
                    </span>
                    <span className="he-card-meta-item">
                        <BsFillPeopleFill />
                        {event.totalRecieved}
                        {event.memberGroup !== 3 ? `/${event.totalMember}` : ''}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ── Main Page ────────────────────────────────────────────────────────────────
function HomeEvent() {
    const { inProgress, instance, accounts } = useMsal();
    const navigate = useNavigate();
    const [allEvents, setAllEvents] = useState<EventItem[]>([]);
    const [stateLoad, setStateLoad] = useState(false);
    const [token, setToken] = useState('');
    const [tokenProfile, setTokenProfile] = useState('');
    const [photoData, setPhotoData] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [search, setSearch] = useState('');
    const [lang, setLang] = useLang();
    const { t } = useLabels('home', token, lang);

    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                setTokenProfile(res.accessTokenProfile);
                try {
                    const r1 = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: 'arraybuffer',
                    });
                    const avatarSrc = 'data:image/jpeg;base64, ' + Buffer.from(r1.data, 'binary').toString('base64');
                    setPhotoData(avatarSrc);
                    sessionStorage.setItem('PhotoData', avatarSrc);
                } catch {}
                try {
                    const r2 = await axios('https://graph.microsoft.com/v1.0/me', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                    });
                    sessionStorage.setItem('Profile', JSON.stringify(r2.data));
                } catch {}
            });
        }
    }, [inProgress]);

    useEffect(() => {
        if (!token) return;
        loadData();
    }, [token]);

    useEffect(() => {
        if (!tokenProfile) return;
        const checkRole_ = async () => {
            try {
                const role = await checkRole(tokenProfile);
                setIsAdmin(role === 'admin');
                setIsSuperAdmin(role === 'superadmin');
            } catch {}
        };
        checkRole_();
    }, [tokenProfile]);

    const loadData = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API_END_POINT + '/v1/Events/events',
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + (sessionStorage.getItem('accessToken') || token),
                    },
                    params: { keyword: '', limit: 500, offset: 0 },
                }
            );
            const list = (response.data.eventsLists ?? response.data ?? []).sort(
                (a: any, b: any) =>
                    new Date(b.createdAt || b.startDate).getTime() -
                    new Date(a.createdAt || a.startDate).getTime()
            );
            setAllEvents(list);
        } catch (err) {
            console.error(err);
        } finally {
            setStateLoad(true);
        }
    };

    const [showAllPast, setShowAllPast] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
    const PAST_PAGE = 8;

    const userEmail = accounts[0]?.username ?? '';
    const isEventOwner = allEvents.some((ev) =>
        ev.owner?.split(';').some((o) => o.trim().toLowerCase() === userEmail.toLowerCase())
    );
    const canCreateEvent = isAdmin || isEventOwner;

    const today = moment.utc().tz('Asia/Bangkok').startOf('day');
    const isHappeningNow = (ev: EventItem) =>
        moment.utc(ev.startDate).tz('Asia/Bangkok').startOf('day').isSameOrBefore(today) &&
        moment.utc(ev.endDate).tz('Asia/Bangkok').startOf('day').isSameOrAfter(today);
    const isUpcoming = (ev: EventItem) => moment.utc(ev.startDate).tz('Asia/Bangkok').startOf('day').isAfter(today);
    const isPast = (ev: EventItem) => moment.utc(ev.endDate).tz('Asia/Bangkok').startOf('day').isBefore(today);

    const getDaysUntil = (dateStr: string): { label: string; type: 'live' | 'upcoming' | 'soon' } => {
        const diff = moment.utc(dateStr).tz('Asia/Bangkok').startOf('day').diff(today, 'days');
        if (diff <= 3) return { label: diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : `${diff} days`, type: 'soon' };
        return { label: `In ${diff} days`, type: 'upcoming' };
    };

    const getDaysAgo = (dateStr: string) => {
        const diff = today.diff(moment(dateStr).startOf('day'), 'days');
        if (diff === 0) return 'Ended today';
        if (diff === 1) return 'Ended yesterday';
        if (diff < 30) return `${diff} days ago`;
        if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
        return `${Math.floor(diff / 365)}y ago`;
    };

    const happeningNow = allEvents.filter(isHappeningNow);
    const upcomingEvents = allEvents
        .filter(isUpcoming)
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    const pastEvents = allEvents
        .filter(isPast)
        .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());

    const q = search.toLowerCase();
    const matchSearch = (ev: EventItem) =>
        ev.eventName.toLowerCase().includes(q) || (ev.description ?? '').toLowerCase().includes(q);
    const filteredHappening = q ? happeningNow.filter(matchSearch) : happeningNow;
    const filteredUpcoming = q ? upcomingEvents.filter(matchSearch) : upcomingEvents;
    const filteredPast = q ? pastEvents.filter(matchSearch) : pastEvents;
    const visiblePast = showAllPast ? filteredPast : filteredPast.slice(0, PAST_PAGE);

    if (!stateLoad) return <LoadingScreen />;

    return (
        <div className="he-page">
            <HomeTopbar photoData={photoData} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} lang={lang} onLangChange={setLang} />

            {/* ── Hero ── */}
            <section className="he-hero" style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/images/bg-home.jpg')` }}>
                <div className="he-hero-inner">
                    <div className="he-hero-content">
                        <div className="he-hero-badge">
                            <MdOutlineEventNote /> {t('hero.badge', 'C&S EVENTS PPV')}
                        </div>
                        <h1 className="he-hero-title">{t('hero.title', 'Discover Company Events')}</h1>
                        <p className="he-hero-sub">
                            {t('hero.subtitle', 'Stay connected with upcoming events, workshops, and activities')}
                        </p>
                        <div className="he-hero-actions">
                            <div className="he-hero-search">
                                <BiSearch className="he-hero-search-icon" />
                                <input
                                    type="text"
                                    placeholder={t('hero.search.placeholder', 'Search events...')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="he-hero-search-input"
                                />
                                {search && (
                                    <button className="he-hero-search-clear" onClick={() => setSearch('')}>
                                        ×
                                    </button>
                                )}
                            </div>
                            {isAdmin && (
                                <a href="/events" className="btn btn-add he-hero-cta">
                                    <BiCalendarPlus /> {t('hero.create_event', 'Create Event')}
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="he-hero-deco" aria-hidden="true" />
                </div>
            </section>

            {/* ── Stats Bar ── */}
            <div className="he-stats">
                <div className="he-stats-inner">
                    <div className="he-stat-item">
                        <span className="he-stat-num">{allEvents.length}</span>
                        <span className="he-stat-label">{t('stats.total', 'Total Events')}</span>
                    </div>
                    <div className="he-stat-divider" />
                    <div className="he-stat-item">
                        <span className="he-stat-num he-stat-num--live">{happeningNow.length}</span>
                        <span className="he-stat-label">{t('stats.happening', 'Happening Now')}</span>
                    </div>
                    <div className="he-stat-divider" />
                    <div className="he-stat-item">
                        <span className="he-stat-num he-stat-num--upcoming">{upcomingEvents.length}</span>
                        <span className="he-stat-label">{t('stats.upcoming', 'Upcoming')}</span>
                    </div>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="he-content">

                {/* Happening Now */}
                {filteredHappening.length > 0 && (
                    <section className="he-section">
                        <div className="he-section-header">
                            <div className="he-section-heading">
                                <span className="he-live-dot" />
                                <h2 className="he-section-title">{t('section.happening', 'Happening Now')}</h2>
                                <span className="he-section-count">{filteredHappening.length}</span>
                            </div>
                            {filteredHappening.length > 5 && (
                                <button className="he-see-all" onClick={() => navigate('/browse/happening')}>
                                    {t('section.see_all', 'See all')} {filteredHappening.length} <BsArrowRight size={12} />
                                </button>
                            )}
                        </div>
                        <div className="he-grid">
                            {filteredHappening.slice(0, 5).map((ev) => (
                                <EventPosterCard
                                    key={ev.eventNumber}
                                    event={ev}
                                    onClick={() => setSelectedEvent(ev)}
                                    badge="LIVE"
                                    badgeType="live"
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Upcoming Events */}
                {filteredUpcoming.length > 0 && (
                    <section className="he-section">
                        <div className="he-section-header">
                            <div className="he-section-heading">
                                <span className="he-section-icon-dot he-section-icon-dot--upcoming" />
                                <h2 className="he-section-title">{t('section.upcoming', 'Upcoming Events')}</h2>
                                <span className="he-section-count">{filteredUpcoming.length}</span>
                            </div>
                            {filteredUpcoming.length > 5 && (
                                <button className="he-see-all" onClick={() => navigate('/browse/upcoming')}>
                                    {t('section.see_all', 'See all')} {filteredUpcoming.length} <BsArrowRight size={12} />
                                </button>
                            )}
                        </div>
                        <div className="he-grid">
                            {filteredUpcoming.slice(0, 5).map((ev) => {
                                const { label, type } = getDaysUntil(ev.startDate);
                                return (
                                    <EventPosterCard
                                        key={ev.eventNumber}
                                        event={ev}
                                        onClick={() => setSelectedEvent(ev)}
                                        badge={label}
                                        badgeType={type}
                                    />
                                );
                            })}
                        </div>
                    </section>
                )}


                {/* Empty State */}
                {allEvents.length === 0 && (
                    <div className="he-empty">
                        <div className="he-empty-icon">🎪</div>
                        <h3>{t('empty.title', 'No Events Yet')}</h3>
                        {isAdmin ? (
                            <>
                                <p>{t('empty.admin_msg', 'Create your first event and start inviting people!')}</p>
                                <a href="/events" className="btn btn-add">
                                    <BiCalendarPlus /> {t('hero.create_event', 'Create Event')}
                                </a>
                            </>
                        ) : (
                            <p>{t('empty.user_msg', 'There are no events available at the moment. Check back later!')}</p>
                        )}
                    </div>
                )}

                {/* Past Events */}
                {filteredPast.length > 0 && (
                    <section className="he-section">
                        <div className="he-section-header">
                            <div className="he-section-heading">
                                <span className="he-section-icon-dot he-section-icon-dot--past" />
                                <h2 className="he-section-title">{t('section.past', 'Past Events')}</h2>
                                <span className="he-section-count">{filteredPast.length}</span>
                            </div>
                        </div>
                        <div className="he-past-list">
                            {visiblePast.map((ev) => (
                                <div
                                    key={ev.eventNumber}
                                    className="he-past-row"
                                    onClick={() => setSelectedEvent(ev)}
                                >
                                    <div className="he-past-thumb">
                                        <img src={getImageSrc(ev.image)} alt={ev.eventName} />
                                    </div>
                                    <div className="he-past-info">
                                        <div className="he-past-name">{ev.eventName}</div>
                                        <div className="he-past-desc">{ev.description}</div>
                                        <div className="he-past-meta">
                                            <span><BsCalendar3 /> {moment.utc(ev.startDate).tz('Asia/Bangkok').format('DD MMM')} – {moment.utc(ev.endDate).tz('Asia/Bangkok').format('DD MMM YYYY')}</span>
                                            <span><BsFillPeopleFill /> {ev.totalRecieved}{ev.memberGroup !== 3 ? `/${ev.totalMember}` : ''} joined</span>
                                        </div>
                                    </div>
                                    <div className="he-past-right">
                                        <span className="he-past-ago">{getDaysAgo(ev.endDate)}</span>
                                        <span className="he-past-badge">{t('past.ended', 'Ended')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {filteredPast.length > PAST_PAGE && (
                            <div className="he-past-more">
                                <button
                                    className="he-past-more-btn"
                                    onClick={() => setShowAllPast((v) => !v)}
                                >
                                    {showAllPast
                                        ? t('past.show_less', 'Show less')
                                        : `${t('past.show_more', 'Show more')} (${filteredPast.length - PAST_PAGE})`}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* No search results */}
                {search &&
                    filteredHappening.length === 0 &&
                    filteredUpcoming.length === 0 &&

                    filteredPast.length === 0 && (
                        <div className="he-empty">
                            <div className="he-empty-icon">🔍</div>
                            <h3>{t('search.no_results', 'No results for')} "{search}"</h3>
                            <p>{t('search.no_results_hint', 'Try a different keyword or clear the search.')}</p>
                            <button
                                className="btn btn-add"
                                onClick={() => setSearch('')}
                                style={{ border: 'none' }}
                            >
                                {t('search.clear', 'Clear Search')}
                            </button>
                        </div>
                    )}
            </div>

            {/* ── Footer ── */}
            <footer className="he-footer">
                <div className="he-footer-inner">
                    <div className="he-footer-brand">
                        <img src="/logo-pandora.png" alt="Pandora" className="he-footer-logo-img" />
                        <span className="he-footer-tagline">{t('footer.tagline', 'Internal event management platform')}</span>
                    </div>
                    <nav className="he-footer-links">
                        <button onClick={() => navigate('/')}>Home</button>
                        {isAdmin && (
                            <button onClick={() => navigate('/myevents')}>My Events</button>
                        )}
                    </nav>
                    <div className="he-footer-copy">
                        © {new Date().getFullYear()} PPV &mdash; All rights reserved
                    </div>
                </div>
            </footer>

            {/* ── Event Detail Modal ── */}
            {selectedEvent && (
                <EventDetailModal
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    onNavigate={() => {
                        setSelectedEvent(null);
                        navigate(`/event/${selectedEvent.eventNumber}`);
                    }}
                    userEmail={accounts[0]?.username ?? ''}
                    isAdmin={isAdmin}
                />
            )}
        </div>
    );
}

export default HomeEvent;
